import { useState } from 'react';
import type { IntegrationLog, IntegrationLogType, IntegrationLogStatus, IntegrationLogFilters } from '@yawi/types';
import {
  RefreshCw,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  MessageSquare,
  Smartphone,
  Bell,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { Button, Card } from '@/core/ui/components';

interface IntegrationLogsProps {
  logs: IntegrationLog[];
  isLoading: boolean;
  totalCount: number;
  filters: IntegrationLogFilters;
  onFilterChange: (filters: IntegrationLogFilters) => void;
  onRefresh: () => void;
}

const typeIcons: Record<IntegrationLogType, React.ReactNode> = {
  EMAIL: <Mail className="w-4 h-4" />,
  SMS: <Smartphone className="w-4 h-4" />,
  WHATSAPP: <MessageSquare className="w-4 h-4" />,
  PUSH: <Bell className="w-4 h-4" />,
  PAYMENT: <CreditCard className="w-4 h-4" />,
};

const typeLabels: Record<IntegrationLogType, string> = {
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
  PUSH: 'Push',
  PAYMENT: 'Pagos',
};

const statusColors: Record<IntegrationLogStatus, string> = {
  SUCCESS: 'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30',
  FAILURE: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30',
  PENDING: 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30',
};

const statusIcons: Record<IntegrationLogStatus, React.ReactNode> = {
  SUCCESS: <CheckCircle className="w-4 h-4" />,
  FAILURE: <XCircle className="w-4 h-4" />,
  PENDING: <Clock className="w-4 h-4" />,
};

export function IntegrationLogs({
  logs,
  isLoading,
  totalCount,
  filters,
  onFilterChange,
  onRefresh,
}: IntegrationLogsProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleTypeFilter = (type: IntegrationLogType | '') => {
    onFilterChange({
      ...filters,
      type: type || undefined,
      page: 1,
    });
  };

  const handleStatusFilter = (status: IntegrationLogStatus | '') => {
    onFilterChange({
      ...filters,
      status: status || undefined,
      page: 1,
    });
  };

  const handlePageChange = (page: number) => {
    onFilterChange({
      ...filters,
      page,
    });
  };

  const totalPages = Math.ceil(totalCount / (filters.limit || 20));
  const currentPage = filters.page || 1;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white/90">
            Logs de Integraciones
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {totalCount} entradas totales
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Filter className="w-4 h-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
          >
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Tipo
              </label>
              <select
                value={filters.type || ''}
                onChange={(e) => handleTypeFilter(e.target.value as IntegrationLogType | '')}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Todos</option>
                <option value="EMAIL">Email</option>
                <option value="SMS">SMS</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="PUSH">Push</option>
                <option value="PAYMENT">Pagos</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Estado
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleStatusFilter(e.target.value as IntegrationLogStatus | '')}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Todos</option>
                <option value="SUCCESS">Exitoso</option>
                <option value="FAILURE">Fallido</option>
                <option value="PENDING">Pendiente</option>
              </select>
            </div>
          </div>
        </Card>
      )}

      {/* Logs Table */}
      {isLoading ? (
        <Card className="p-6">
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
          </div>
        </Card>
      ) : logs.length === 0 ? (
        <Card className="p-6">
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-white/90">
              No hay logs
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              La actividad de integraciones aparecerá aquí
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Proveedor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Detalles
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">

                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {logs.map((log) => (
                  <>
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer"
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 dark:text-gray-400">
                            {typeIcons[log.type]}
                          </span>
                          <span className="text-sm text-gray-800 dark:text-white/90">
                            {typeLabels[log.type]}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {log.provider}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusColors[log.status]}`}>
                          {statusIcons[log.status]}
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {log.recipient && (
                            <span className="text-gray-600 dark:text-gray-300">{log.recipient}</span>
                          )}
                          {log.subject && (
                            <span className="text-gray-500 dark:text-gray-400 ml-2 text-xs">
                              ({log.subject})
                            </span>
                          )}
                          {log.errorMessage && (
                            <span className="text-red-600 dark:text-red-400">{log.errorMessage}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <div>{formatDate(log.createdAt)}</div>
                          {log.latencyMs && (
                            <div className="text-xs">{log.latencyMs}ms</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {expandedLog === log.id ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </td>
                    </tr>
                    {expandedLog === log.id && (
                      <tr key={`${log.id}-details`}>
                        <td colSpan={6} className="px-4 py-4 bg-gray-50 dark:bg-gray-800/30">
                          <div className="space-y-3">
                            {log.requestPayload && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Request Payload
                                </h4>
                                <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(log.requestPayload, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.responsePayload && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Response Payload
                                </h4>
                                <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(log.responsePayload, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
